import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { label: "Services", id: "services" },
  { label: "Process", id: "process" },
  { label: "Pricing", id: "pricing" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

export default function Header({ onOpenForm }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNavClick = useCallback(
    (id) => {
      setMobileOpen(false);
      if (location.pathname === "/") {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      } else {
        navigate(`/#${id}`);
      }
    },
    [location.pathname, navigate]
  );

  const goHome = () => {
    setMobileOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white border-b border-slate-200 backdrop-blur-xl shadow-sm"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <button
            onClick={goHome}
            data-testid="header-logo"
            className="flex items-center gap-2 group"
          >
            <Shield className="w-7 h-7 text-[#1E3A8A] group-hover:text-[#2563EB] transition-colors" />
            <span className="font-heading text-lg sm:text-xl font-extrabold tracking-tight text-[#0F172A]">
              Trademark<span className="text-[#1E3A8A]"> Officers</span>
            </span>
          </button>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link.id)}
                data-testid={`nav-link-${link.id}`}
                className="text-sm font-medium text-slate-600 hover:text-[#1E3A8A] transition-colors relative after:absolute after:bottom-0 after:left-0 after:w-0 after:h-0.5 after:bg-[#1E3A8A] after:transition-all hover:after:w-full"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:block">
            <Button
              onClick={onOpenForm}
              data-testid="header-cta-btn"
              className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white font-medium px-6 hover:scale-[1.02] active:scale-[0.98] transition-all"
            >
              Protect My Brand
            </Button>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-slate-700"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white/95 backdrop-blur-xl border-b border-slate-200 shadow-lg animate-fade-in"
        >
          <div className="px-4 py-4 space-y-1">
            {NAV_LINKS.map((link) => (
              <button
                key={link.id}
                onClick={() => handleNavClick(link.id)}
                data-testid={`mobile-nav-link-${link.id}`}
                className="block w-full text-left px-4 py-3 text-sm font-medium text-slate-700 hover:text-[#1E3A8A] hover:bg-slate-50 rounded-lg transition-colors"
              >
                {link.label}
              </button>
            ))}
            <div className="pt-2 px-4">
              <Button
                onClick={() => {
                  setMobileOpen(false);
                  onOpenForm();
                }}
                data-testid="mobile-cta-btn"
                className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
              >
                Protect My Brand
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
